import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import ReactFlow, {
  Background,
  Controls,
  Handle,
  MiniMap,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
} from "reactflow"
import reactflowStyles from "reactflow/dist/style.css"

// reactflow ships its CSS as a package file; esbuild loads it as a text string
// (--loader:.css=text) so it never collides with the Tailwind application.css.
// Inject it once, globally.
function ensureStyles() {
  if (document.getElementById("reactflow-inline-styles")) return
  const el = document.createElement("style")
  el.id = "reactflow-inline-styles"
  el.textContent = reactflowStyles
  document.head.appendChild(el)
}

const STEP_TYPES = [
  "message",
  "quick_reply",
  "delay",
  "tag_contact",
  "capture_email",
  "booking_link",
  "end",
]

// The single field the inline editor exposes per step type.
const PRIMARY_FIELD = {
  message: { key: "text", label: "Message", kind: "textarea" },
  quick_reply: { key: "text", label: "Prompt", kind: "textarea" },
  capture_email: { key: "prompt", label: "Ask for email", kind: "textarea" },
  booking_link: { key: "url", label: "Booking URL", kind: "text" },
  delay: { key: "seconds", label: "Wait (seconds)", kind: "number" },
  tag_contact: { key: "tags", label: "Tags (comma-separated)", kind: "tags" },
  end: null,
}

// ── Graph <-> React Flow conversion ──────────────────────────────────────────
// Stored schema: { nodes: [{id, type, data, position?}], edges: [{source, target, sourceHandle?}] }
function toReactFlow(graph) {
  const nodes = (graph.nodes || []).map((n, i) => ({
    id: n.id,
    type: n.type === "start" ? "input" : "wf",
    position: n.position || { x: 60, y: 40 + i * 130 },
    data: { ...(n.data || {}), wfType: n.type },
  }))
  const edges = (graph.edges || []).map((e, i) => ({
    id: e.id || `e${i}-${e.source}-${e.target}`,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle || null,
    label: e.sourceHandle || undefined,
  }))
  return { nodes, edges }
}

function toGraph(nodes, edges) {
  return {
    nodes: nodes.map((n) => {
      const { wfType, ...data } = n.data || {}
      return { id: n.id, type: wfType || (n.type === "input" ? "start" : "message"), data, position: n.position }
    }),
    edges: edges.map((e) => {
      const edge = { source: e.source, target: e.target }
      if (e.sourceHandle) edge.sourceHandle = e.sourceHandle
      return edge
    }),
  }
}

// ── Custom node ───────────────────────────────────────────────────────────────
function StepNode({ id, data }) {
  const field = PRIMARY_FIELD[data.wfType]
  const update = data.onChange
  const value =
    field == null
      ? ""
      : field.kind === "tags"
      ? (data.tags || []).join(", ")
      : data[field.key] ?? ""

  return React.createElement(
    "div",
    { className: "wf-node", style: nodeStyle(data.wfType) },
    React.createElement(Handle, { type: "target", position: "top" }),
    React.createElement("div", { style: { fontWeight: 600, fontSize: 12, marginBottom: 4 } },
      humanize(data.wfType)),
    field &&
      React.createElement(fieldTag(field), {
        className: "nodrag",
        style: fieldStyle,
        type: field.kind === "number" ? "number" : "text",
        value,
        placeholder: field.label,
        onChange: (ev) => update && update(id, field, ev.target.value),
      }),
    data.wfType === "quick_reply" &&
      React.createElement("div", { style: { fontSize: 10, color: "#6b7280", marginTop: 4 } },
        "Label edges with each option."),
    data.wfType !== "end" && React.createElement(Handle, { type: "source", position: "bottom" }),
  )
}

function fieldTag(field) {
  return field.kind === "textarea" ? "textarea" : "input"
}
const fieldStyle = {
  width: 180,
  fontSize: 11,
  padding: "4px 6px",
  border: "1px solid #d1d5db",
  borderRadius: 4,
  resize: "vertical",
}
function nodeStyle(type) {
  const colors = {
    message: "#eef2ff",
    quick_reply: "#ecfeff",
    delay: "#fef9c3",
    tag_contact: "#f0fdf4",
    capture_email: "#fdf2f8",
    booking_link: "#eff6ff",
    end: "#f3f4f6",
  }
  return {
    background: colors[type] || "#fff",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: 10,
    minWidth: 200,
  }
}
function humanize(s) {
  return String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

// ── Editor ────────────────────────────────────────────────────────────────────
export default function FlowEditor({ graph, saveUrl, csrf }) {
  ensureStyles()
  const initial = useMemo(() => toReactFlow(graph || { nodes: [], edges: [] }), [])
  const [nodes, setNodes] = useState(initial.nodes)
  const [edges, setEdges] = useState(initial.edges)
  const [status, setStatus] = useState("Saved")
  const saveTimer = useRef(null)
  const idSeq = useRef(1)

  const nodeTypes = useMemo(() => ({ wf: StepNode }), [])

  const scheduleSave = useCallback((nextNodes, nextEdges) => {
    setStatus("Saving…")
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(saveUrl, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": csrf,
          },
          body: JSON.stringify({ graph: toGraph(nextNodes, nextEdges) }),
        })
        setStatus(res.ok ? "Saved" : "Save failed")
      } catch (e) {
        setStatus("Save failed")
      }
    }, 700)
  }, [saveUrl, csrf])

  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const next = applyNodeChanges(changes, nds)
      scheduleSave(next, edges)
      return next
    })
  }, [edges, scheduleSave])

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => {
      const next = applyEdgeChanges(changes, eds)
      scheduleSave(nodes, next)
      return next
    })
  }, [nodes, scheduleSave])

  const onConnect = useCallback((conn) => {
    setEdges((eds) => {
      const next = addEdge({ ...conn }, eds)
      scheduleSave(nodes, next)
      return next
    })
  }, [nodes, scheduleSave])

  const editField = useCallback((nodeId, field, raw) => {
    setNodes((nds) => {
      const next = nds.map((n) => {
        if (n.id !== nodeId) return n
        let val = raw
        if (field.kind === "number") val = Number(raw) || 0
        if (field.kind === "tags") val = raw.split(",").map((s) => s.trim()).filter(Boolean)
        return { ...n, data: { ...n.data, [field.key]: val } }
      })
      scheduleSave(next, edges)
      return next
    })
  }, [edges, scheduleSave])

  // Inject the onChange handler into every node's data so the custom node can
  // write back into editor state.
  const decoratedNodes = useMemo(
    () => nodes.map((n) => ({ ...n, data: { ...n.data, onChange: editField } })),
    [nodes, editField]
  )

  const addNode = useCallback((type) => {
    const id = `n_${Date.now()}_${idSeq.current++}`
    const node = {
      id,
      type: "wf",
      position: { x: 320, y: 80 + nodes.length * 40 },
      data: { wfType: type },
    }
    setNodes((nds) => {
      const next = [...nds, node]
      scheduleSave(next, edges)
      return next
    })
  }, [nodes, edges, scheduleSave])

  return React.createElement(
    "div",
    { style: { height: "100%", display: "flex", flexDirection: "column" } },
    React.createElement(
      "div",
      { style: toolbarStyle },
      React.createElement("span", { style: { fontSize: 11, color: "#9ca3af", marginRight: 4 } }, "Add step"),
      ...STEP_TYPES.map((t) =>
        React.createElement("button", {
          key: t,
          type: "button",
          onClick: () => addNode(t),
          style: chipStyle,
        }, "+ " + humanize(t))
      ),
      React.createElement("span", { style: { marginLeft: "auto", fontSize: 11, color: "#6b7280" } }, status),
    ),
    React.createElement(
      "div",
      { style: { flex: 1 } },
      React.createElement(
        ReactFlow,
        {
          nodes: decoratedNodes,
          edges,
          nodeTypes,
          onNodesChange,
          onEdgesChange,
          onConnect,
          fitView: true,
          deleteKeyCode: ["Backspace", "Delete"],
        },
        React.createElement(Background, null),
        React.createElement(Controls, null),
        React.createElement(MiniMap, { pannable: true, zoomable: true }),
      )
    )
  )
}

const toolbarStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  flexWrap: "wrap",
  padding: 8,
  borderBottom: "1px solid #f3f4f6",
}
const chipStyle = {
  fontSize: 11,
  fontWeight: 500,
  padding: "4px 8px",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  background: "#fff",
  cursor: "pointer",
}
